import React from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";

const UserConfirmedPage = () => {
  return (
    <PageBg>
      <PageContainer>
        <Header>
          <h1>Download the Products</h1>
          <p>
            Depending on your subscription type (ask your administrator), you might need to
            download a client app. Otherwise go to platform.emotii.ai to manage your products.
          </p>
        </Header>

        <Intro>
          <h2>Get Started</h2>
          <p>
            Choose the right app for your device and start using the product right away.
          </p>
        </Intro>

        <CardsGrid>
          <DownloadCard
            icon={<HeadsetIcon />}
            title="IM Bots"
            description="Connect with your team through popular messaging platforms."
            links={[
              { label: "MS Teams", href: "https://appsource.microsoft.com/en-us/product/office/WA200007550?tab=Overview" },
              { label: "Slack", href: "https://emotii-slack-bot-cdf4d6dbfa34.herokuapp.com/slack/install" },
            ]}
          />

          <DownloadCard
            icon={<MobileIcon />}
            title="Mobile Apps"
            description="Stay connected on the go with our mobile applications."
            links={[
              { label: "Android", href: "https://play.google.com/store/apps/details?id=com.emotii.emotii&pli=1" },
              { label: "iOS", href: "https://apps.apple.com/us/app/emotii-realtime-translator/id6476883749" },
            ]}
          />

          <DownloadCard
            icon={<GlobeIcon />}
            title="Web Apps"
            description="Access everything directly from your web browser."
            links={[{ label: "Web App", href: "https://app.emotii.ai" }]}
          />

          <DownloadCard
            icon={<DesktopIcon />}
            title="Desktop Apps"
            description="Full-featured desktop applications for your computer"
            links={[
              { label: "Windows", href: "https://emotii.s3.eu-west-1.amazonaws.com/desktop-apps/windows/emotii.exe" },
              { label: "Mac (Silicon)", href: "https://emotii.s3.eu-west-1.amazonaws.com/desktop-apps/mac/apple-silicon/emotii.dmg" },
              { label: "Mac (Intel)", href: "https://emotii.s3.eu-west-1.amazonaws.com/desktop-apps/mac/intel/emotii.dmg" },
            ]}
          />
        </CardsGrid>
      </PageContainer>
    </PageBg>
  );
};

export default UserConfirmedPage;

// Presentational components
const PageBg = styled.div`
  background: ${lightThemeColors.menuBackground};
  height: 100%;
  width: 100%;
  overflow-y: auto;
`;

const PageContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 48px 24px 80px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 0; /* spacing handled by Intro margin + container gap */

  h1 {
    /* Match Figma: Inter, 600, 30px/38px, #181D27, centered */
    color: #181D27;
    font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, sans-serif;
    font-weight: 600;
    font-size: 30px;
    line-height: 38px;
    margin-bottom: 4px; /* 4px gap to main subtitle */
  }

  p {
    color: #535862; /* Gray/600 */
    font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, sans-serif;
    font-weight: 400;
    font-size: 20px;
    line-height: 30px;
    margin: 0; /* no extra bottom margin */
  }
`;

const Intro = styled.div`
  /* Ensure 64px from Header subtitle to this heading: container gap (24) + this margin-top (40) */
  margin-top: 40px;
  text-align: left;

  h2 {
    /* Inter, 600, 20px/38px, #181D27 */
    color: #181D27;
    font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, sans-serif;
    font-weight: 600;
    font-size: 20px;
    line-height: 38px;
  }

  p {
    /* Inter 400, 16px/24px, Gray/600 */
    color: #535862;
    font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    margin: 0; /* rely on container gap for spacing to cards */
  }
`;

const CardsGrid = styled.div`
  margin-top: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: 820px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const CardContainer = styled.div`
  /* Card spec: radius 10px, padding 24px, gap 32px, white background */
  background: #ffffff;
  border-radius: 10px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: flex-start; /* left align */
`;

const IconBox = styled.div`
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
`;

const CardContent = styled.div`
  flex: 1;
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0; /* manage precise spacing via margins below */

  h3 {
    margin: 0 0 8px 0; /* 8px to subtitle */
    color: ${lightThemeColors.darkColor};
    font-size: 18px;
    font-weight: 700;
  }

  p {
    margin: 0 0 20px 0; /* 20px to links */
    color: ${lightThemeColors.darkColor50};
    font-size: 14px;
  }

  /* Link row */
  div {
    display: flex;
    justify-content: flex-start;
    gap: 8px; /* spacing between individual links when multiple */
    flex-wrap: wrap;
  }

  a {
    color: #6941C6; /* Brand/700 */
    text-decoration: underline;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
  }
`;

// Small helper to render a card from data
const DownloadCard = ({ icon, title, description, links = [] }) => (
  <CardContainer>
    <IconBox>{icon}</IconBox>
    <CardContent>
      <h3>{title}</h3>
      {description && <p>{description}</p>}
      <div>
        {links.map((l, idx) => (
          <a key={idx} href={l.href} target="_blank" rel="noreferrer">
            {l.label}
          </a>
        ))}
      </div>
    </CardContent>
  </CardContainer>
);

// Simple inline icons to keep dependencies minimal
const HeadsetIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="48" height="48" rx="10" fill="#9E77ED"/>
    <path d="M15.334 30V24C15.334 21.6131 16.2822 19.3239 17.97 17.636C19.6579 15.9482 21.947 15 24.334 15C26.7209 15 29.0101 15.9482 30.6979 17.636C32.3858 19.3239 33.334 21.6131 33.334 24V30M33.334 31C33.334 31.5304 33.1233 32.0391 32.7482 32.4142C32.3731 32.7893 31.8644 33 31.334 33H30.334C29.8036 33 29.2948 32.7893 28.9198 32.4142C28.5447 32.0391 28.334 31.5304 28.334 31V28C28.334 27.4696 28.5447 26.9609 28.9198 26.5858C29.2948 26.2107 29.8036 26 30.334 26H33.334V31ZM15.334 31C15.334 31.5304 15.5447 32.0391 15.9198 32.4142C16.2948 32.7893 16.8036 33 17.334 33H18.334C18.8644 33 19.3731 32.7893 19.7482 32.4142C20.1233 32.0391 20.334 31.5304 20.334 31V28C20.334 27.4696 20.1233 26.9609 19.7482 26.5858C19.3731 26.2107 18.8644 26 18.334 26H15.334V31Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const MobileIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="48" height="48" rx="10" fill="#9E77ED"/>
    <rect x="14" y="9" width="20" height="30" rx="3" stroke="white" strokeWidth="2"/>
    <circle cx="24" cy="33" r="1.8" fill="white"/>
  </svg>
);

const GlobeIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="48" height="48" rx="10" fill="#9E77ED"/>
    <path d="M24.334 14.25C18.958 14.25 14.584 18.624 14.584 24C14.584 29.376 18.958 33.75 24.334 33.75C29.71 33.75 34.084 29.376 34.084 24C34.084 18.624 29.71 14.25 24.334 14.25ZM31.226 19.477C30.09 19.729 28.939 19.917 27.782 20.043C27.538 18.5 27.149 17.111 26.614 16.082C28.525 16.634 30.156 17.852 31.226 19.477ZM26.584 24C26.584 24.844 26.543 25.621 26.478 26.342C25.051 26.251 23.618 26.251 22.19 26.342C22.124 25.621 22.084 24.845 22.084 24C22.084 23.161 22.124 22.39 22.189 21.672C22.903 21.72 23.618 21.75 24.334 21.75C25.05 21.75 25.765 21.72 26.479 21.672C26.544 22.389 26.584 23.161 26.584 24ZM24.334 32.25C23.78 32.25 22.849 30.712 22.378 27.835C23.681 27.755 24.988 27.755 26.291 27.835C25.819 30.711 24.888 32.249 24.335 32.249L24.334 32.25ZM22.378 20.164C22.85 17.288 23.781 15.75 24.334 15.75C24.887 15.75 25.819 17.288 26.29 20.164C24.987 20.244 23.681 20.244 22.378 20.164ZM22.055 16.081C21.519 17.11 21.131 18.499 20.887 20.042C19.73 19.917 18.58 19.728 17.443 19.476C18.513 17.851 20.144 16.633 22.055 16.081ZM16.085 23.999C16.085 22.884 16.31 21.821 16.713 20.85C18.027 21.16 19.359 21.387 20.7 21.538C20.625 22.344 20.585 23.171 20.585 23.999C20.585 24.827 20.626 25.66 20.701 26.47C19.36 26.617 18.027 26.839 16.713 27.149C16.31 26.178 16.085 25.115 16.085 24V23.999ZM17.443 28.522C18.58 28.27 19.73 28.082 20.887 27.956C21.131 29.499 21.52 30.888 22.055 31.917C20.144 31.365 18.513 30.147 17.443 28.522ZM26.615 31.917C27.151 30.888 27.539 29.499 27.783 27.956C28.94 28.081 30.09 28.27 31.227 28.522C30.157 30.147 28.526 31.365 26.615 31.917ZM27.969 26.469C28.044 25.66 28.085 24.829 28.085 23.999C28.085 23.169 28.045 22.344 27.97 21.538C29.311 21.387 30.643 21.16 31.957 20.85C32.36 21.821 32.585 22.884 32.585 23.999C32.585 25.114 32.36 26.177 31.957 27.148C30.643 26.838 29.31 26.616 27.969 26.469Z" fill="white"/>
  </svg>
);

const DesktopIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="48" height="48" rx="10" fill="#9E77ED"/>
    <rect x="9" y="12" width="30" height="20" rx="3" stroke="white" strokeWidth="2"/>
    <path d="M18 36H30M21 32H27" stroke="white" strokeWidth="2"/>
  </svg>
);
